/**
 * Main application theme management hook
 * Provides theme switching functionality for the application
 *
 * Uses a custom event (APP_THEME_CHANGE_EVENT) to sync React state across
 * multiple useTheme() instances in the same tab. DOM class and localStorage
 * are shared naturally; the event keeps each instance's currentTheme and
 * effectiveTheme in sync without requiring a React Context provider.
 */

import { useState, useEffect, useCallback } from 'react';
import { Brand } from '@enfomentis/shared/config';

export type AppTheme = 'light' | 'dark' | 'auto';

export interface AppThemeState {
  currentTheme: AppTheme;
  effectiveTheme: 'light' | 'dark';
  setTheme: (theme: AppTheme) => void;
}

/** Custom event name for same-tab theme sync between useTheme() instances */
export const APP_THEME_CHANGE_EVENT = 'app-theme-change';

/**
 * Main application theme hook
 * Manages theme state and applies theme classes to document
 */
export const useTheme = (): AppThemeState => {
  // Initialize theme from localStorage or default to 'auto'
  const [currentTheme, setCurrentTheme] = useState<AppTheme>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(`${Brand.cachePrefix}-theme`) as AppTheme;
      return saved || 'auto';
    }
    return 'auto';
  });

  // Determine effective theme (auto follows system preference)
  const [effectiveTheme, setEffectiveTheme] = useState<'light' | 'dark'>('light');

  // Internal: apply theme to localStorage, DOM, and effectiveTheme state.
  // Does NOT dispatch the custom event — used by mount effect and sync listener
  // to avoid redundant event loops.
  const applyTheme = useCallback((theme: AppTheme) => {
    // Save to localStorage
    localStorage.setItem(`${Brand.cachePrefix}-theme`, theme);

    // Apply theme to document
    const html = document.documentElement;

    if (theme === 'dark') {
      html.classList.add('dark');
      setEffectiveTheme('dark');
    } else if (theme === 'light') {
      html.classList.remove('dark');
      setEffectiveTheme('light');
    } else if (theme === 'auto') {
      // Follow system preference
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      if (prefersDark) {
        html.classList.add('dark');
        setEffectiveTheme('dark');
      } else {
        html.classList.remove('dark');
        setEffectiveTheme('light');
      }
    }
  }, []);

  // Public: update React state, apply theme, and notify other instances
  const setTheme = useCallback((theme: AppTheme) => {
    setCurrentTheme(theme);
    applyTheme(theme);
    window.dispatchEvent(
      new CustomEvent(APP_THEME_CHANGE_EVENT, { detail: { theme } }),
    );
  }, [applyTheme]);

  // Sync with other useTheme() instances in the same tab
  useEffect(() => {
    const handleThemeSync = (e: Event): void => {
      const theme = (e as CustomEvent).detail.theme as AppTheme;
      setCurrentTheme(theme);
    };
    window.addEventListener(APP_THEME_CHANGE_EVENT, handleThemeSync);
    return () => window.removeEventListener(APP_THEME_CHANGE_EVENT, handleThemeSync);
  }, []);

  // Listen for system theme changes when in auto mode
  useEffect(() => {
    if (currentTheme === 'auto') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

      const handleChange = (e: MediaQueryListEvent) => {
        const html = document.documentElement;
        if (e.matches) {
          html.classList.add('dark');
          setEffectiveTheme('dark');
        } else {
          html.classList.remove('dark');
          setEffectiveTheme('light');
        }
      };

      mediaQuery.addEventListener('change', handleChange);

      return () => {
        mediaQuery.removeEventListener('change', handleChange);
      };
    }

    // Return empty cleanup function when not in auto mode
    return () => {};
  }, [currentTheme]);

  // Apply initial theme on mount and when currentTheme changes (e.g. from sync)
  useEffect(() => {
    applyTheme(currentTheme);
  }, [currentTheme, applyTheme]);

  return {
    currentTheme,
    effectiveTheme,
    setTheme
  };
};
